import { useMemo } from "react";
import moment from "moment";
import { MiButton, MiDateRangePicker } from "@miplatform/components";
import { ButtonVariants, CalendarType, Colors } from "@miplatform/types";
import {
  getDate,
  getDateISO,
  isToday,
  shouldDisableWeekend,
} from "@miplatform/utils";
import { mdiCalendarRefresh } from "@mdi/js";
import { DatePicker } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { useCalendarContext } from "../../providers";

export function NhcBoardHeaderCenter() {
  const { dateRange, date, setCalendarDate, setDateRange, calendarConfig } =
    useCalendarContext();

  const { calendarType } = calendarConfig || {};

  const showDateRangePicker = calendarType === CalendarType.Kanban;
  const showDatePicker =
    calendarType === CalendarType.Scheduler ||
    calendarType === CalendarType.PriorityScheduler;

  const pickerDateRange = useMemo(
    () => ({
      startDate: moment(dateRange.startDate).toDate(),
      endDate: dateRange.endDate
        ? moment(dateRange.endDate).toDate()
        : moment(dateRange.startDate).toDate(),
    }),
    [dateRange.startDate, dateRange.endDate]
  );

  if (showDateRangePicker) {
    return (
      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        <MiDateRangePicker
          onDateChange={(
            start: moment.Moment | null,
            end: moment.Moment | null
          ) => {
            const toISO = (d: Date | moment.Moment) => moment(d).toISOString();
            if (!start) return;
            setDateRange({
              startDate: toISO(start),
              endDate: end ? toISO(end) : toISO(start),
            });
          }}
          dateRange={pickerDateRange}
          maxSelectionEnd={4}
          calendarCount={1}
          showThisWeekButton={false}
          dateFormat="MMM DD"
        />
        <MiButton
          title="Today"
          variant={ButtonVariants.Contained}
          color={Colors.Primary}
          startIcon={{
            path: mdiCalendarRefresh,
          }}
          onClick={() =>
            setDateRange({
              startDate: getDateISO(getDate()),
              endDate: getDateISO(getDate()),
            })
          }
          disabled={
            isToday(dateRange.startDate) &&
            isToday(dateRange.endDate ?? dateRange.startDate)
          }
          data-testid="reset-button"
        />
      </Box>
    );
  }

  if (showDatePicker) {
    return (
      <DatePicker
        date={date}
        setDate={setCalendarDate}
        onDateChange={setCalendarDate}
        shouldDisableDate={shouldDisableWeekend}
      />
    );
  }

  return null;
}
